import { useState } from 'react';
import { ArrowLeft, Copy, Share2, Users, DollarSign, Gift, Check, Award, TrendingUp, MessageCircle, Mail, Facebook, Twitter, Linkedin, Heart, Star, Trophy, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { copyToClipboard } from '../utils/clipboard';
import { AtootbandanLogo } from './AtootbandanLogo';
import { Footer } from './Footer';

interface ReferEarnPageProps {
  onBack: () => void;
}

interface Referral {
  id: number;
  name: string;
  email: string;
  status: 'pending' | 'registered' | 'premium' | 'matched';
  date: string;
  earnings: number;
}

const sampleReferrals: Referral[] = [
  { id: 1, name: "Priya Sharma", email: "priya@example.com", status: "premium", date: "2024-11-20", earnings: 500 },
  { id: 2, name: "Amit Patel", email: "amit@example.com", status: "matched", date: "2024-11-18", earnings: 1000 },
  { id: 3, name: "Sneha Kumar", email: "sneha@example.com", status: "registered", date: "2024-11-15", earnings: 100 },
  { id: 4, name: "Rahul Singh", email: "rahul@example.com", status: "pending", date: "2024-11-12", earnings: 0 },
];

export default function ReferEarnPage({ onBack }: ReferEarnPageProps) {
  const [referrals] = useState<Referral[]>(sampleReferrals);
  const [copiedCode, setCopiedCode] = useState(false);
  
  const referralCode = "SOUL2024XYZ";
  const referralLink = `https://atootbandan.com/ref/${referralCode}`;
  
  const totalEarnings = referrals.reduce((sum, ref) => sum + ref.earnings, 0);
  const totalReferrals = referrals.length;
  const successfulReferrals = referrals.filter(r => r.status === 'premium' || r.status === 'matched').length;
  const pendingEarnings = referrals.filter(r => r.status === 'pending' || r.status === 'registered')
    .reduce((sum, ref) => sum + 400, 0);

  const handleCopyCode = () => {
    copyToClipboard(referralCode);
    setCopiedCode(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleCopyLink = () => {
    copyToClipboard(referralLink);
    toast.success('Referral link copied!');
  };

  const handleShare = (platform: string) => {
    const message = `Join me on Atootbandan - India's Most Trusted Matrimonial Platform! Use my code ${referralCode} and get special benefits. ${referralLink}`;
    
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=Join Atootbandan&body=${encodeURIComponent(message)}`;
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`, '_blank');
        break;
    }
  };

  const getStatusBadge = (status: string) => {
    const configs = {
      pending: { color: 'bg-gray-100 text-gray-700', label: 'Pending' },
      registered: { color: 'bg-blue-100 text-blue-700', label: 'Registered' },
      premium: { color: 'bg-purple-100 text-purple-700', label: 'Premium' },
      matched: { color: 'bg-green-100 text-green-700', label: 'Matched' }
    };
    const config = configs[status as keyof typeof configs];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="border-b bg-white/80 border-gray-200 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="cursor-pointer" onClick={onBack}>
              <AtootbandanLogo size="sm" showText={true} />
            </div>
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <ArrowLeft className="size-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm mb-6">
            <Gift className="size-5 text-rose-500" />
            <span className="text-sm font-medium text-gray-700">Refer & Earn Program</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-rose-500 to-amber-400 bg-clip-text text-transparent leading-tight">
            Share Love,<br />Earn Rewards
          </h1>
          
          <p className="text-lg md:text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Help your friends find their life partner and earn exciting rewards for every successful referral!
          </p>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-rose-500 to-amber-400 bg-clip-text text-transparent mb-2">
                ₹{totalEarnings}
              </div>
              <div className="text-sm text-gray-600">Total Earnings</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{totalReferrals}</div>
              <div className="text-sm text-gray-600">Total Referrals</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl md:text-4xl font-bold text-green-600 mb-2">{successfulReferrals}</div>
              <div className="text-sm text-gray-600">Successful</div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="text-3xl md:text-4xl font-bold text-amber-600 mb-2">₹{pendingEarnings}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600 text-lg">Start earning in three simple steps</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center group">
              <div className="relative mb-6">
                <div className="size-20 md:size-24 rounded-full bg-gradient-to-br from-rose-500 to-amber-400 text-white flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <Share2 className="size-10 md:size-12" />
                </div>
                <div className="absolute -top-2 -right-2 size-8 rounded-full bg-white shadow-md flex items-center justify-center font-bold text-rose-500">
                  1
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Share Your Link</h3>
              <p className="text-gray-600">Share your unique referral code with friends and family via WhatsApp, Email, or Social Media</p>
            </div>
            
            <div className="text-center group">
              <div className="relative mb-6">
                <div className="size-20 md:size-24 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-white flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <Users className="size-10 md:size-12" />
                </div>
                <div className="absolute -top-2 -right-2 size-8 rounded-full bg-white shadow-md flex items-center justify-center font-bold text-blue-500">
                  2
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">They Sign Up</h3>
              <p className="text-gray-600">Your friends register using your referral code and complete their profile</p>
            </div>
            
            <div className="text-center group">
              <div className="relative mb-6">
                <div className="size-20 md:size-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-400 text-white flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform">
                  <DollarSign className="size-10 md:size-12" />
                </div>
                <div className="absolute -top-2 -right-2 size-8 rounded-full bg-white shadow-md flex items-center justify-center font-bold text-green-500">
                  3
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">You Earn Rewards</h3>
              <p className="text-gray-600">Earn instant cashback when they upgrade to premium or find their match</p>
            </div>
          </div>
        </div>
      </section>

      {/* Earning Structure */}
      <section className="py-16 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Earning Structure</h2>
            <p className="text-gray-600 text-lg">Multiple ways to earn rewards</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-200">
              <div className="size-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-4">
                <Zap className="size-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Registration Bonus</h3>
              <div className="text-4xl font-bold text-blue-600 mb-3">₹100</div>
              <p className="text-gray-600">When your friend successfully registers and completes their profile</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-purple-200">
              <div className="size-16 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-4">
                <Star className="size-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Premium Upgrade</h3>
              <div className="text-4xl font-bold text-purple-600 mb-3">₹500</div>
              <p className="text-gray-600">When they purchase any premium subscription plan</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-green-200">
              <div className="size-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-4">
                <Heart className="size-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Successful Match</h3>
              <div className="text-4xl font-bold text-green-600 mb-3">₹1,000</div>
              <p className="text-gray-600">When they find their perfect life partner through Atootbandan</p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Code Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-rose-500 to-amber-400 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="text-center mb-8">
              <Trophy className="size-16 mx-auto mb-4 opacity-90" />
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Your Referral Code</h2>
              <p className="text-white/90 text-lg">Share this code to start earning</p>
            </div>
            
            <div className="space-y-6">
              {/* Referral Code */}
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                <label className="text-white/90 text-sm mb-2 block">Referral Code</label>
                <div className="flex gap-3">
                  <Input 
                    value={referralCode} 
                    readOnly 
                    className="text-center font-mono text-2xl bg-white text-gray-900 border-0 h-14"
                  />
                  <Button 
                    onClick={handleCopyCode}
                    className={`h-14 px-6 ${copiedCode ? 'bg-green-600 hover:bg-green-700' : 'bg-white text-rose-500 hover:bg-gray-100'}`}
                  >
                    {copiedCode ? (
                      <>
                        <Check className="size-5 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="size-5 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Referral Link */}
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                <label className="text-white/90 text-sm mb-2 block">Referral Link</label>
                <div className="flex gap-3">
                  <Input 
                    value={referralLink} 
                    readOnly 
                    className="bg-white text-gray-900 border-0 h-12"
                  />
                  <Button 
                    onClick={handleCopyLink}
                    className="h-12 px-6 bg-white text-rose-500 hover:bg-gray-100"
                  >
                    <Copy className="size-5" />
                  </Button>
                </div>
              </div>

              {/* Share Buttons */}
              <div>
                <p className="text-white/90 text-sm mb-3">Share via</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Button 
                    onClick={() => handleShare('whatsapp')}
                    className="h-12 bg-white/20 hover:bg-white/30 text-white border-white/30"
                    variant="outline"
                  >
                    <MessageCircle className="size-5 mr-2" />
                    WhatsApp
                  </Button>
                  <Button 
                    onClick={() => handleShare('email')}
                    className="h-12 bg-white/20 hover:bg-white/30 text-white border-white/30"
                    variant="outline"
                  >
                    <Mail className="size-5 mr-2" />
                    Email
                  </Button>
                  <Button 
                    onClick={() => handleShare('facebook')}
                    className="h-12 bg-white/20 hover:bg-white/30 text-white border-white/30"
                    variant="outline"
                  >
                    <Facebook className="size-5 mr-2" />
                    Facebook
                  </Button>
                  <Button 
                    onClick={() => handleShare('twitter')}
                    className="h-12 bg-white/20 hover:bg-white/30 text-white border-white/30"
                    variant="outline"
                  >
                    <Twitter className="size-5 mr-2" />
                    Twitter
                  </Button>
                  <Button 
                    onClick={() => handleShare('linkedin')}
                    className="h-12 bg-white/20 hover:bg-white/30 text-white border-white/30 col-span-2 md:col-span-1"
                    variant="outline"
                  >
                    <Linkedin className="size-5 mr-2" />
                    LinkedIn
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral History */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Your Referral History</CardTitle>
              <CardDescription>Track all your referrals and earnings</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="all">
                <TabsList className="mb-6">
                  <TabsTrigger value="all">All ({referrals.length})</TabsTrigger>
                  <TabsTrigger value="successful">Successful ({successfulReferrals})</TabsTrigger>
                  <TabsTrigger value="pending">Pending ({referrals.length - successfulReferrals})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="all" className="space-y-4">
                  {referrals.map(referral => (
                    <div key={referral.id} className="flex items-center justify-between p-5 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="font-semibold text-lg">{referral.name}</p>
                        <p className="text-sm text-gray-600">{referral.email}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(referral.date).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(referral.status)}
                        <p className="text-xl font-bold mt-2 text-green-600">
                          {referral.earnings > 0 ? `₹${referral.earnings}` : '-'}
                        </p>
                      </div>
                    </div>
                  ))}
                </TabsContent>
                
                <TabsContent value="successful" className="space-y-4">
                  {referrals.filter(r => r.status === 'premium' || r.status === 'matched').map(referral => (
                    <div key={referral.id} className="flex items-center justify-between p-5 bg-green-50 rounded-xl hover:bg-green-100 transition-colors border border-green-200">
                      <div>
                        <p className="font-semibold text-lg">{referral.name}</p>
                        <p className="text-sm text-gray-600">{referral.email}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(referral.date).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(referral.status)}
                        <p className="text-xl font-bold mt-2 text-green-600">₹{referral.earnings}</p>
                      </div>
                    </div>
                  ))}
                </TabsContent>
                
                <TabsContent value="pending" className="space-y-4">
                  {referrals.filter(r => r.status === 'pending' || r.status === 'registered').map(referral => (
                    <div key={referral.id} className="flex items-center justify-between p-5 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors border border-amber-200">
                      <div>
                        <p className="font-semibold text-lg">{referral.name}</p>
                        <p className="text-sm text-gray-600">{referral.email}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(referral.date).toLocaleDateString('en-IN')}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(referral.status)}
                        <p className="text-sm text-gray-500 mt-2">Potential: ₹400</p>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Progress Card */}
          <Card className="border-0 shadow-xl mt-6">
            <CardHeader>
              <CardTitle className="text-xl">Progress to Gold Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Successful Referrals</span>
                  <span className="font-bold text-lg">{successfulReferrals}/10</span>
                </div>
                <Progress value={successfulReferrals * 10} className="h-3" />
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Trophy className="size-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-amber-900 mb-1">Unlock Gold Status!</p>
                      <p className="text-sm text-amber-700">
                        Refer {10 - successfulReferrals} more successful users to unlock Gold tier and earn 20% bonus on all future referrals!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
